import {useState} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {SearchSmIcon, Users02Icon} from '@/components/ui/icon/Icon'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {PreviewSampleRecipient} from '@/features/mailer/types'
import {PREVIEW_SAMPLE_SIZE} from '@/features/mailer/constants'
import {
    StyledCounterBand,
    StyledCounterCard,
    StyledCounterIconCircle,
    StyledCounterInfo,
    StyledCounterTexts,
    StyledKindBadge,
    StyledPreviewEmpty,
    StyledPreviewHeader,
    StyledPreviewIdentity,
    StyledPreviewList,
    StyledPreviewMore,
    StyledPreviewRow,
    StyledPreviewSection,
    StyledPreviewToggle,
    StyledSkeletonRow
} from '@/features/mailer/components/mailer-recipient-counter/style'

interface MailerRecipientCounterProps {
    total: number
    patientsCount: number | null
    consultantsCount: number | null
    isLoading: boolean
    sample: PreviewSampleRecipient[]
}

// Live recipient counter (design §5.8): summary band with deduped total and
// per-category split, plus an expandable verification panel with the first
// recipients (avatar, identity, category) and the size of the remainder.
export const MailerRecipientCounter = ({
    total,
    patientsCount,
    consultantsCount,
    isLoading,
    sample
}: MailerRecipientCounterProps) => {
    const {t} = useTranslation()
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)

    const splitParts = [
        patientsCount !== null ? t('mailer:counter_split_patients', {count: patientsCount}) : null,
        consultantsCount !== null ? t('mailer:counter_split_staff', {count: consultantsCount}) : null
    ].filter(Boolean)

    // The BE dedupes emails shared across the two lists (contract §1):
    // surfacing the overlap explains why total < patients + consultants.
    const overlap =
        patientsCount !== null && consultantsCount !== null ? Math.max(0, patientsCount + consultantsCount - total) : 0
    const remaining = Math.max(0, total - sample.length)
    const isRefreshing = isLoading && sample.length > 0

    return (
        <StyledCounterCard>
            <StyledCounterBand>
                <StyledCounterInfo>
                    <StyledCounterIconCircle>
                        {isLoading ? <Spinner size={16} /> : <Users02Icon />}
                    </StyledCounterIconCircle>
                    <StyledCounterTexts>
                        <p>
                            <strong>{total.toLocaleString('it-IT')}</strong> {t('mailer:counter_recipients_match')}
                        </p>
                        <small>
                            {splitParts.length > 0 ? splitParts.join(' + ') : t('mailer:counter_none_selected')}
                            {overlap > 0 && ` · ${t('mailer:counter_overlap', {count: overlap})}`}
                        </small>
                    </StyledCounterTexts>
                </StyledCounterInfo>
                <StyledPreviewToggle
                    type="button"
                    aria-expanded={isPreviewOpen}
                    aria-controls="mailer-recipients-preview"
                    onClick={() => setIsPreviewOpen(previous => !previous)}
                >
                    <SearchSmIcon />
                    {isPreviewOpen ? t('mailer:counter_preview_hide') : t('mailer:counter_preview_show')}
                </StyledPreviewToggle>
            </StyledCounterBand>

            {isPreviewOpen && (
                <StyledPreviewSection id="mailer-recipients-preview">
                    <StyledPreviewHeader>
                        <strong>{t('mailer:preview_header')}</strong>
                        {total > 0 && (
                            <span>
                                {t('mailer:preview_showing', {
                                    count: Math.min(sample.length, total),
                                    total: total.toLocaleString('it-IT')
                                })}
                            </span>
                        )}
                    </StyledPreviewHeader>

                    {isLoading && sample.length === 0 ? (
                        Array.from({length: PREVIEW_SAMPLE_SIZE}, (_, index) => (
                            <StyledSkeletonRow key={index}>
                                <Skeleton width={24} height={24} />
                                <Skeleton height={14} width="60%" />
                                <Skeleton height={18} width={56} />
                            </StyledSkeletonRow>
                        ))
                    ) : sample.length === 0 ? (
                        <StyledPreviewEmpty>{t('mailer:preview_empty')}</StyledPreviewEmpty>
                    ) : (
                        <>
                            <StyledPreviewList $isRefreshing={isRefreshing}>
                                {sample.map((recipient, index) => (
                                    <StyledPreviewRow key={`${recipient.email}-${index}`}>
                                        <Avatar size="xs" name={recipient.name || recipient.email} />
                                        <StyledPreviewIdentity>
                                            <strong className={recipient.name ? undefined : 'missing'}>
                                                {recipient.name || t('mailer:preview_name_missing')}
                                            </strong>
                                            <span>{recipient.email}</span>
                                        </StyledPreviewIdentity>
                                        <StyledKindBadge $kind={recipient.kind}>
                                            {recipient.kind === 'patient'
                                                ? t('mailer:preview_kind_patient')
                                                : t('mailer:preview_kind_consultant')}
                                        </StyledKindBadge>
                                    </StyledPreviewRow>
                                ))}
                            </StyledPreviewList>
                            {remaining > 0 && (
                                <StyledPreviewMore>
                                    {t('mailer:preview_more', {
                                        count: remaining,
                                        formatted: remaining.toLocaleString('it-IT')
                                    })}
                                </StyledPreviewMore>
                            )}
                        </>
                    )}
                </StyledPreviewSection>
            )}
        </StyledCounterCard>
    )
}

MailerRecipientCounter.displayName = 'MailerRecipientCounter'
